import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Container,
  Stack,
  Typography,
} from "@mui/material";

/*
=========================================================
 MUNDO VIRTUAL ECOSYSTEM — IMPROVED VERSION
=========================================================

- Copy this same file into Big Dog Bone and City Farm.
- It does not modify login, JWT, tokens, users, or databases.
- It only displays the four ecosystem access points.
- It does not require additional packages beyond @mui/material.
- The real links and logos are already configured.
=========================================================
*/

const PROJECTS = [
  {
    id: "big-dog-bone",
    name: "BIG DOG BONE",
    description:
      "Collect, upgrade, and compete inside the Mundo Virtual NFT universe.",
    image: "https://www.bigdogbonegame.com/static/media/logo.5acc4af4e6491040e775.png",
    url: "https://www.bigdogbonegame.com/ecosystem/login",
    buttonLabel: "ENTER GAME",
    type: "NFT GAME",
    accentColor: "#ffad32",
    accentRgb: "255,173,50",
    external: false,
  },
  {
    id: "city-farm",
    name: "CITY FARM",
    description:
      "Grow crops, produce resources, and build your own farm economy.",
    image: "https://res.cloudinary.com/dkm71aag8/image/upload/v1755052377/logos/logo_xyjkop.png",
    url: "https://www.cityfarmgame.com/ecosystem/login",
    buttonLabel: "ENTER GAME",
    type: "WEB GAME",
    accentColor: "#72e34b",
    accentRgb: "114,227,75",
    external: false,
  },
  {
    id: "nova",
    name: "NOVA",
    description:
      "Join adventures, events, and rewards through our community.",
    image: "https://res.cloudinary.com/dkm71aag8/image/upload/v1755052358/logos/logo_nova_hv5mrd.png",
    url: "https://discord.gg/GJS6MtKB2x",
    buttonLabel: "OPEN NOVA",
    type: "DISCORD",
    accentColor: "#55a8ff",
    accentRgb: "85,168,255",
    external: true,
  },
  {
    id: "airdropbot",
    name: "AIRDROPBOT",
    description:
      "Discover events, airdrops, and special ecosystem activities.",
    image: "https://res.cloudinary.com/dkm71aag8/image/upload/v1755052363/logos/mundoairdrops_s6mwyo.png",
    url: "https://discord.gg/GJS6MtKB2x",
    buttonLabel: "OPEN AIRDROPBOT",
    type: "DISCORD",
    accentColor: "#c77dff",
    accentRgb: "199,125,255",
    external: true,
  },
];

const openProject = (project) => {
  if (!project?.url) return;

  if (project.external) {
    window.open(project.url, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.assign(project.url);
};

const forceTextColor = (value) => ({
  color: `${value} !important`,
  WebkitTextFillColor: `${value} !important`,
});

function ProjectCard({ project, index }) {
  const [imageError, setImageError] = useState(false);

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: { xs: 350, sm: 365, lg: 390 },
        borderRadius: { xs: "24px", md: "30px" },
        color: "#fff",
        border: `1px solid rgba(${project.accentRgb}, .42)`,
        background:
          "linear-gradient(180deg, rgba(19,30,49,.96) 0%, rgba(5,10,19,.99) 100%)",
        boxShadow: `
          0 25px 70px rgba(0,0,0,.42),
          0 0 35px rgba(${project.accentRgb}, .10)
        `,
        transform: "translateZ(0)",
        animation: `cardEntrance .7s ease ${index * 0.08}s both`,
        transition:
          "transform .28s ease, border-color .28s ease, box-shadow .28s ease",
        "&:hover": {
          transform: "translateY(-10px)",
          borderColor: project.accentColor,
          boxShadow: `
            0 35px 90px rgba(0,0,0,.55),
            0 0 45px rgba(${project.accentRgb}, .22)
          `,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(circle at 50% 6%, rgba(${project.accentRgb}, .22), transparent 38%),
            linear-gradient(120deg, rgba(255,255,255,.035), transparent 42%)
          `,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          left: "18%",
          right: "18%",
          bottom: -60,
          height: 100,
          pointerEvents: "none",
          borderRadius: "50%",
          background: `rgba(${project.accentRgb}, .22)`,
          filter: "blur(42px)",
        },
      }}
    >
      <CardActionArea
        onClick={() => openProject(project)}
        aria-label={`Open ${project.name}`}
        sx={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          minHeight: "inherit",
          p: { xs: 1.55, md: 1.75 },
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
          borderRadius: "inherit",
          "&:focus-visible": {
            outline: `3px solid ${project.accentColor}`,
            outlineOffset: "-5px",
          },
        }}
      >
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.15 }}
          >
            <Box
              sx={{
                px: 1.2,
                py: 0.65,
                borderRadius: "999px",
                border: `1px solid rgba(${project.accentRgb}, .42)`,
                background: `rgba(${project.accentRgb}, .10)`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 950,
                  letterSpacing: ".13em",
                  ...forceTextColor(project.accentColor),
                }}
              >
                {project.type}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.7} alignItems="center">
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "#4cff87",
                  boxShadow: "0 0 12px rgba(76,255,135,.9)",
                }}
              />
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 850,
                  letterSpacing: ".09em",
                  ...forceTextColor("rgba(255,255,255,.72)"),
                }}
              >
                DIRECT ACCESS
              </Typography>
            </Stack>
          </Stack>

          <Box
            sx={{
              position: "relative",
              height: { xs: 155, sm: 165, md: 175 },
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
              borderRadius: "18px",
              border: "1px solid rgba(255,255,255,.08)",
              background: `
                radial-gradient(circle at 50% 40%, rgba(${project.accentRgb}, .18), transparent 55%),
                linear-gradient(180deg, rgba(255,255,255,.045), rgba(255,255,255,.012))
              `,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,.05)",
              "&::before": {
                content: '""',
                position: "absolute",
                width: 118,
                height: 118,
                borderRadius: "50%",
                border: `1px solid rgba(${project.accentRgb}, .20)`,
                boxShadow: `
                  0 0 0 18px rgba(${project.accentRgb}, .035),
                  0 0 0 36px rgba(${project.accentRgb}, .018)
                `,
              },
            }}
          >
            {!imageError ? (
              <Box
                component="img"
                src={project.image}
                alt={`Logo ${project.name}`}
                onError={() => setImageError(true)}
                sx={{
                  position: "relative",
                  zIndex: 2,
                  width: "88%",
                  height: "88%",
                  objectFit: "contain",
                  filter: `drop-shadow(0 16px 26px rgba(${project.accentRgb}, .34))`,
                  transition: "transform .32s ease, filter .32s ease",
                  ".MuiCardActionArea-root:hover &": {
                    transform: "scale(1.075)",
                    filter: `drop-shadow(0 20px 34px rgba(${project.accentRgb}, .48))`,
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  position: "relative",
                  zIndex: 2,
                  width: 92,
                  height: 92,
                  borderRadius: "25px",
                  display: "grid",
                  placeItems: "center",
                  border: `1px solid rgba(${project.accentRgb}, .50)`,
                  background: `rgba(${project.accentRgb}, .12)`,
                  boxShadow: `0 0 30px rgba(${project.accentRgb}, .20)`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 25,
                    fontWeight: 1000,
                    ...forceTextColor(project.accentColor),
                  }}
                >
                  {project.name
                    .split(" ")
                    .map((word) => word[0])
                    .join("")
                    .slice(0, 3)}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography
            component="h2"
            sx={{
              mt: 1.45,
              textAlign: "center",
              fontSize: { xs: 21, md: 23 },
              lineHeight: 1,
              fontWeight: 1000,
              letterSpacing: "-.035em",
              ...forceTextColor("#ffffff"),
            }}
          >
            {project.name}
          </Typography>

          <Typography
            sx={{
              mt: 0.85,
              px: { xs: 0.25, md: 0.5 },
              minHeight: { xs: 42, md: 44 },
              textAlign: "center",
              fontSize: 12.5,
              lineHeight: 1.42,
              ...forceTextColor("rgba(255,255,255,.76)"),
            }}
          >
            {project.description}
          </Typography>
        </Box>

        <Button
          component="div"
          fullWidth
          disableElevation
          sx={{
            mt: 1.35,
            minHeight: 43,
            borderRadius: "15px",
            ...forceTextColor("#ffffff"),
            fontSize: 12,
            fontWeight: 1000,
            letterSpacing: ".08em",
            border: `1px solid ${project.accentColor}`,
            background: `linear-gradient(
              180deg,
              rgba(${project.accentRgb}, .28),
              rgba(${project.accentRgb}, .10)
            )`,
            boxShadow: `
              inset 0 1px 0 rgba(255,255,255,.13),
              0 10px 24px rgba(${project.accentRgb}, .12)
            `,
            transition: "transform .2s ease, background .2s ease",
            "&:hover": {
              background: `linear-gradient(
                180deg,
                rgba(${project.accentRgb}, .42),
                rgba(${project.accentRgb}, .17)
              )`,
            },
            ".MuiCardActionArea-root:hover &": {
              transform: "translateY(-2px)",
            },
          }}
        >
          {project.buttonLabel}
          <Box
            component="span"
            sx={{
              ml: 1,
              fontSize: 18,
              lineHeight: 1,
              transition: "transform .2s ease",
              ".MuiCardActionArea-root:hover &": {
                transform: "translateX(4px)",
              },
            }}
          >
            →
          </Box>
        </Button>
      </CardActionArea>
    </Card>
  );
}

export default function EcosistemaMundoVirtual() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        color: "#fff",
        background:
          "linear-gradient(180deg, #02050b 0%, #07101e 48%, #02050a 100%)",
        "@keyframes floatGlow": {
          "0%, 100%": {
            transform: "translate3d(0, 0, 0) scale(1)",
          },
          "50%": {
            transform: "translate3d(0, -18px, 0) scale(1.04)",
          },
        },
        "@keyframes cardEntrance": {
          from: {
            opacity: 0,
            transform: "translateY(22px)",
          },
          to: {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
        "@keyframes heroEntrance": {
          from: {
            opacity: 0,
            transform: "translateY(14px)",
          },
          to: {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            animation: "none !important",
            transition: "none !important",
          },
        },
      }}
    >
      {/* DECORATIVE BACKGROUND */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.022) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(255,255,255,.022) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,.92), transparent 92%)",
        }}
      />

      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: "-180px",
          left: "-180px",
          width: 520,
          height: 520,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,161,31,.19), transparent 68%)",
          filter: "blur(18px)",
          animation: "floatGlow 8s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: "10%",
          right: "-220px",
          width: 620,
          height: 620,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(91,92,255,.17), transparent 68%)",
          filter: "blur(24px)",
          animation: "floatGlow 10s ease-in-out infinite reverse",
          pointerEvents: "none",
        }}
      />

      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          left: "50%",
          bottom: "-280px",
          transform: "translateX(-50%)",
          width: 1050,
          height: 560,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(22,138,255,.15), transparent 70%)",
          filter: "blur(30px)",
          pointerEvents: "none",
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 2,
          py: { xs: 1.5, sm: 2, md: 2.5 },
          pb: { xs: 3, md: 4 },
        }}
      >
        {/* HEADER */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          sx={{
            p: { xs: 1.25, sm: 1.45 },
            borderRadius: { xs: "20px", md: "24px" },
            border: "1px solid rgba(255,255,255,.09)",
            background: "rgba(5,11,21,.64)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 18px 55px rgba(0,0,0,.28)",
          }}
        >
          <Stack direction="row" spacing={1.4} alignItems="center">
            <Box
              sx={{
                width: { xs: 46, sm: 52 },
                height: { xs: 46, sm: 52 },
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                borderRadius: "16px",
                fontSize: 21,
                fontWeight: 1000,
                letterSpacing: "-2px",
                ...forceTextColor("#191004"),
                background:
                  "linear-gradient(135deg, #fff7bf 0%, #f3bc49 43%, #9d5700 100%)",
                boxShadow:
                  "0 0 28px rgba(242,180,57,.32), inset 0 1px 0 rgba(255,255,255,.65)",
              }}
            >
              MV
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 14, sm: 16 },
                  lineHeight: 1,
                  fontWeight: 1000,
                  letterSpacing: ".10em",
                  ...forceTextColor("#ffffff"),
                }}
              >
                MUNDO VIRTUAL
              </Typography>

              <Typography
                sx={{
                  mt: 0.65,
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 900,
                  letterSpacing: ".15em",
                  ...forceTextColor("#efb447"),
                }}
              >
                GAMING ECOSYSTEM
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          >
            {["HIVE", "NFT", "GAMES", "COMMUNITY"].map((item) => (
              <Box
                key={item}
                sx={{
                  px: 1.25,
                  py: 0.65,
                  borderRadius: "999px",
                  border: "1px solid rgba(255,255,255,.075)",
                  background: "rgba(255,255,255,.025)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 900,
                    letterSpacing: ".10em",
                    ...forceTextColor("rgba(255,255,255,.72)"),
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>

        {/* HERO */}
        <Box
          sx={{
            maxWidth: 980,
            mx: "auto",
            mt: { xs: 3.2, sm: 3.8, md: 4.5 },
            mb: { xs: 3, md: 3.8 },
            textAlign: "center",
            animation: "heroEntrance .7s ease both",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 1.35,
              px: 1.25,
              py: 0.8,
              borderRadius: "999px",
              border: "1px solid rgba(239,180,71,.24)",
              background: "rgba(239,180,71,.07)",
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#efb447",
                boxShadow: "0 0 12px rgba(239,180,71,.9)",
              }}
            />
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 950,
                letterSpacing: ".16em",
                ...forceTextColor("#efc46f"),
              }}
            >
              CHOOSE YOUR EXPERIENCE
            </Typography>
          </Box>

          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: "2.35rem",
                sm: "3.35rem",
                md: "4.55rem",
              },
              lineHeight: { xs: 0.96, md: 0.9 },
              fontWeight: 1000,
              letterSpacing: "-.055em",
              textTransform: "uppercase",
              ...forceTextColor("#f7f9fc"),
              textShadow: "0 14px 45px rgba(0,0,0,.72)",
            }}
          >
            ECOSYSTEM
          </Typography>

          <Typography
            component="div"
            sx={{
              mt: 0.7,
              fontSize: {
                xs: "2.2rem",
                sm: "3.2rem",
                md: "4.35rem",
              },
              lineHeight: { xs: 0.98, md: 0.93 },
              fontWeight: 1000,
              letterSpacing: "-.055em",
              textTransform: "uppercase",
              background:
                "linear-gradient(180deg, #fff4b3 0%, #efbd52 45%, #a45f06 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 12px 28px rgba(221,151,22,.18))",
            }}
          >
            MUNDO VIRTUAL
          </Typography>

          <Typography
            sx={{
              maxWidth: 720,
              mx: "auto",
              mt: 1.35,
              px: { xs: 1, sm: 0 },
              fontSize: { xs: 14, sm: 15.5, md: 16.5 },
              lineHeight: 1.48,
              ...forceTextColor("rgba(255,255,255,.78)"),
            }}
          >
            Four experiences connected by one community.
            Choose the project you want to explore and begin your adventure.
          </Typography>
        </Box>

        {/* PROJECTS */}
        <Box
          sx={{
            width: { xs: "68%", sm: "46%", md: "30%" },
            height: "1px",
            mx: "auto",
            mb: { xs: 2.3, md: 2.8 },
            background:
              "linear-gradient(90deg, transparent, rgba(239,180,71,.65), transparent)",
            boxShadow: "0 0 18px rgba(239,180,71,.22)",
          }}
        />

        <Box
          component="section"
          aria-label="Mundo Virtual projects"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            gap: { xs: 1.7, md: 1.9 },
          }}
        >
          {PROJECTS.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </Box>

        {/* FOOTER MESSAGE */}
        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            mx: "auto",
            maxWidth: 900,
            p: { xs: 2, sm: 2.5 },
            borderRadius: "18px",
            border: "1px solid rgba(239,180,71,.22)",
            background:
              "linear-gradient(180deg, rgba(12,21,35,.74), rgba(5,10,18,.72))",
            backdropFilter: "blur(16px)",
            boxShadow: "0 22px 55px rgba(0,0,0,.28)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 14, md: 16 },
              lineHeight: 1.6,
              ...forceTextColor("rgba(255,255,255,.78)"),
            }}
          >
            Big Dog Bone and City Farm take you to their official access pages.
            NOVA and AirdropBot open safely in a new browser tab.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mt: 3,
            px: 0.5,
            ...forceTextColor("rgba(255,255,255,.48)"),
          }}
        >
          <Typography sx={{ fontSize: 11 }}>
            © {new Date().getFullYear()} Mundo Virtual
          </Typography>

          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: ".12em",
            }}
          >
            HIVE · NFT · GAMING · COMMUNITY
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
